import { CURRENCY_CODES } from '../contract/regional';
import { reportPreferenceIssues } from '../contract/report';
import { ISSUE_REASONS, type PreferenceValues } from '../contract/types';
import { numberFormat } from './intl-cache';

/**
 * Money, in the currency the row is denominated in and the layout the user reads.
 *
 * The two are independent, and conflating them is the usual mistake: a Mexican user looking at a
 * US invoice should see US dollars written the Mexican way, not pesos and not American punctuation.
 * `Intl` places the symbol, spaces it and groups the digits from the locale; the currency decides
 * only which symbol and how many decimals.
 *
 * TODO: Ask product whether a currency that matches the reader's locale shows the symbol
 * (`$1,234.50`) and one that does not shows the ISO code (`USD 1,234.50`). Keep the current
 * `Intl` output until that decision.
 */

/** ISO 4217 codes an amount may be denominated in. */
export const SUPPORTED_CURRENCY_CODES: ReadonlySet<string> = new Set(
  CURRENCY_CODES
);

export type MoneyFormatOptions = {
  /**
   * ISO 4217 code for this particular amount, when the row carries one.
   *
   * Omit it, or pass one outside `SUPPORTED_CURRENCY_CODES`, and the result is
   * `''`. The value is matched exactly against `SUPPORTED_CURRENCY_CODES`. A
   * different case, surrounding whitespace, or any other code is reported once.
   */
  readonly code?: string;
  /**
   * Fractional digits.
   *
   * Omit it. Codes in `SUPPORTED_CURRENCY_CODES` use two decimal places. Forcing a
   * count is for the rare column that has to align against something else.
   */
  readonly digits?: number;
};

/**
 * Which currency an amount is in.
 *
 * A code in `SUPPORTED_CURRENCY_CODES` formats as itself. Anything else, and a missing code,
 * yields `undefined`. A present unsupported code is reported once rather than
 * thrown over. Currency codes travel in row data, and row data is exactly what a
 * display layer must not take a page down over.
 *
 * @param code - The code this particular amount carries, if any.
 * @param field - What to attribute an unsupported code to in the report.
 * @returns The code to format with, or `undefined` when none is supported.
 */
export const currencyCodeFor = (
  code: string | undefined,
  field: string
): string | undefined => {
  if (code !== undefined && SUPPORTED_CURRENCY_CODES.has(code)) return code;

  if (code !== undefined) {
    reportPreferenceIssues([
      {
        field,
        received: code,
        reason: ISSUE_REASONS.UNRECOGNISED_CODE,
        resolvedTo: '',
      },
    ]);
  }

  return undefined;
};

/**
 * Formats an amount of money.
 *
 * Omit the code, or pass one outside `SUPPORTED_CURRENCY_CODES`, and the result
 * is `''`. A present unsupported code is reported once, rather than throwing.
 * An explicit supported code formats as itself. Currency codes travel in row
 * data, and row data is exactly what a display layer must not take a page down over.
 *
 * A non-finite amount returns `''` and is reported once. The result is what a
 * screen renders, so `$NaN` is not a useful diagnostic.
 *
 * @param preferences - Supplies the locale.
 * @param value - The amount.
 * @param options - An explicit currency or digit count.
 * @returns The formatted amount, such as `$12.40` or `12,40 $ US`, or `''` without a supported code.
 */
export const formatMoney = (
  preferences: PreferenceValues,
  value: number,
  options?: MoneyFormatOptions
): string => {
  const currency = currencyCodeFor(options?.code, 'formatMoney.code');
  if (currency === undefined) return '';

  if (!Number.isFinite(value)) {
    reportPreferenceIssues([
      {
        field: 'formatMoney.value',
        received: String(value),
        reason: ISSUE_REASONS.NON_FINITE,
        resolvedTo: '',
      },
    ]);
    return '';
  }

  const { digits } = options ?? {};

  return numberFormat(preferences.locale, {
    style: 'currency',
    currency,
    ...(digits === undefined
      ? {}
      : { minimumFractionDigits: digits, maximumFractionDigits: digits }),
  }).format(value);
};
